

import { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useWorkspaceStore, useDocumentStore, useUIStore, useAuthStore } from "@/stores";
import { api } from "@/lib/api-client";
import { DocumentTree } from "@/components/documents/DocumentTree";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  DropdownMenuSub,
  DropdownMenuSubTrigger,
  DropdownMenuSubContent,
} from "@/components/ui/dropdown-menu";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  ChevronDown,
  ChevronRight,
  Plus,
  Search,
  Settings,
  LogOut,
  User,
  Folder,
  FileText,
  Star,
  Archive,
  MoreHorizontal,
  Download,
  Moon,
  Sun,
  Monitor,
  FolderPlus,
  FilePlus,
  Upload,
  Pencil,
  Trash2,
  LayoutGrid,
  Key,
} from "lucide-react";
import { useTheme } from "@/lib/theme-provider";
import { cn } from "@/lib/utils";
import { toast } from "sonner";

export function Sidebar() {
  const navigate = useNavigate();
  
  const { user, logout } = useAuthStore();
  const { workspaces, currentWorkspace, setCurrentWorkspace, addWorkspace, setWorkspaces } = useWorkspaceStore();
  const { documents, currentDocument, setCurrentDocument, searchQuery, setSearchQuery } = useDocumentStore();
  const { toggleSidebar, setCommandPaletteOpen } = useUIStore();
  const { setTheme: setNextTheme } = useTheme();

  const [showNewWorkspace, setShowNewWorkspace] = useState(false);
  const [newWorkspaceName, setNewWorkspaceName] = useState("");
  const [showImportDialog, setShowImportDialog] = useState(false);
  const [editingWorkspace, setEditingWorkspace] = useState<typeof workspaces[0] | null>(null);
  const [editWorkspaceName, setEditWorkspaceName] = useState("");
  const [showDeleteConfirm, setShowDeleteConfirm] = useState<typeof workspaces[0] | null>(null);

  // Filter documents for favorites
  const favoriteDocs = useMemo(() => {
    return documents.filter((d) => d.isFavorite && !d.isArchived);
  }, [documents]);

  // Create workspace
  const createWorkspace = async () => {
    if (!newWorkspaceName.trim()) {
      toast.error("Ingresa un nombre para el workspace");
      return;
    }

    try {
      const data = await api.post<{ success: boolean; workspace: any }>('/workspaces', { name: newWorkspaceName });
      if (data.success) {
        addWorkspace(data.workspace);
        setCurrentWorkspace(data.workspace);
        setNewWorkspaceName("");
        setShowNewWorkspace(false);
        toast.success("Workspace creado");
      }
    } catch (error) {
      toast.error("Error al crear workspace");
    }
  };

  // Update workspace
  const updateWorkspace = async () => {
    if (!editingWorkspace || !editWorkspaceName.trim()) {
      toast.error("Ingresa un nombre para el workspace");
      return;
    }

    try {
      const data = await api.put<{ success: boolean; workspace: any }>('/workspaces', { id: editingWorkspace.id, name: editWorkspaceName });
      if (data.success) {
        useWorkspaceStore.getState().updateWorkspace(editingWorkspace.id, { name: editWorkspaceName });
        setEditingWorkspace(null);
        setEditWorkspaceName("");
        toast.success("Workspace renombrado");
      }
    } catch (error) {
      toast.error("Error al renombrar workspace");
    }
  };

  // Delete workspace
  const deleteWorkspace = async () => {
    if (!showDeleteConfirm) return;

    try {
      const data = await api.delete<{ success: boolean; error?: string }>(`/workspaces?id=${showDeleteConfirm.id}`);
      if (data.success) {
        useWorkspaceStore.getState().removeWorkspace(showDeleteConfirm.id);
        
        // Refresh workspaces from server
        const wsData = await api.get<{ success: boolean; workspaces: any[] }>('/workspaces');
        if (wsData.success) {
          setWorkspaces(wsData.workspaces);
          // Select first workspace if current was deleted
          if (!wsData.workspaces.find((w: any) => w.id === showDeleteConfirm.id)) {
            setCurrentWorkspace(wsData.workspaces[0] || null);
          }
        }
        
        setShowDeleteConfirm(null);
        toast.success("Workspace eliminado");
      } else {
        toast.error(data.error || "Error al eliminar workspace");
      }
    } catch (error) {
      toast.error("Error al eliminar workspace");
    }
  };

  // Handle logout
  const handleLogout = async () => {
    try {
      await api.post('/auth/logout', {});
      logout();
      toast.success("Sesión cerrada");
    } catch (error) {
      toast.error("Error al cerrar sesión");
    }
  };

  return (
    <aside className="w-72 border-r bg-muted/30 flex flex-col h-full">
      {/* Header */}
      <div className="p-3 border-b">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" className="w-full justify-between">
              <div className="flex items-center gap-2">
                <span className="text-lg">{currentWorkspace?.icon || "📁"}</span>
                <span className="truncate font-medium">{currentWorkspace?.name || "Workspace"}</span>
              </div>
              <ChevronDown className="w-4 h-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start" className="w-56">
            <DropdownMenuLabel>Workspaces</DropdownMenuLabel>
            <DropdownMenuSeparator />
            {workspaces.map((ws) => (
              <DropdownMenuSub key={ws.id}>
                <DropdownMenuSubTrigger
                  onClick={() => setCurrentWorkspace(ws)}
                  className={cn(ws.id === currentWorkspace?.id && "bg-accent")}
                >
                  <span className="mr-2">{ws.icon || "📁"}</span>
                  {ws.name}
                  {ws.isDefault && (
                    <span className="ml-auto text-xs text-muted-foreground">default</span>
                  )}
                </DropdownMenuSubTrigger>
                <DropdownMenuSubContent>
                  {!ws.isDefault && (
                    <>
                      <DropdownMenuItem
                        onClick={() => {
                          setEditingWorkspace(ws);
                          setEditWorkspaceName(ws.name);
                        }}
                      >
                        <Pencil className="w-4 h-4 mr-2" />
                        Renombrar
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        onClick={() => setShowDeleteConfirm(ws)}
                        className="text-destructive"
                      >
                        <Trash2 className="w-4 h-4 mr-2" />
                        Eliminar
                      </DropdownMenuItem>
                    </>
                  )}
                </DropdownMenuSubContent>
              </DropdownMenuSub>
            ))}
            <DropdownMenuSeparator />
            <Dialog open={showNewWorkspace} onOpenChange={setShowNewWorkspace}>
              <DialogTrigger asChild>
                <DropdownMenuItem onSelect={(e) => e.preventDefault()}>
                  <Plus className="w-4 h-4 mr-2" />
                  Nuevo Workspace
                </DropdownMenuItem>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Crear nuevo workspace</DialogTitle>
                  <DialogDescription>
                    Un workspace es como un vault de Obsidian donde organizas tus documentos
                  </DialogDescription>
                </DialogHeader>
                <Input
                  placeholder="Nombre del workspace"
                  value={newWorkspaceName}
                  onChange={(e) => setNewWorkspaceName(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && createWorkspace()}
                />
                <DialogFooter>
                  <Button variant="outline" onClick={() => setShowNewWorkspace(false)}>
                    Cancelar
                  </Button>
                  <Button onClick={createWorkspace}>Crear</Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      {/* Search - opens command palette */}
      <div className="p-2">
        <div className="relative">
          <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Buscar (Cmd+K)..."
            className="pl-8 h-9 cursor-pointer"
            readOnly
            onClick={() => setCommandPaletteOpen(true)}
          />
        </div>
      </div>

      {/* Tasks Button */}
      <div className="px-2 pb-1">
        <Button
          variant="ghost"
          className="w-full justify-start h-9 text-sm font-normal"
          onClick={() => window.location.href = "/tasks"}
        >
          <LayoutGrid className="w-4 h-4 mr-2" />
          Tareas
        </Button>
      </div>

      {/* Search */}
      <div className="border-t my-1"></div>

      {/* Document Tree */}
      <div className="flex-1 overflow-hidden">
        <DocumentTree />
      </div>

      {/* Favorites section */}
      {favoriteDocs.length > 0 && (
        <div className="border-t p-2 max-h-32">
          <div className="flex items-center gap-2 px-2 py-1 text-xs font-medium text-muted-foreground">
            <Star className="w-3 h-3" />
            Favoritos
          </div>
          <ScrollArea className="h-20">
            {favoriteDocs.slice(0, 5).map((doc) => (
              <div
                key={doc.id}
                className="flex items-center gap-2 px-2 py-1 rounded-md hover:bg-accent cursor-pointer text-sm"
                  onClick={() => {
                    useDocumentStore.getState().setCurrentDocument(doc);
                    navigate("/");
                  }}
              >
                <span>{doc.icon || "📄"}</span>
                <span className="truncate">{doc.title}</span>
              </div>
            ))}
          </ScrollArea>
        </div>
      )}

      {/* User menu */}
      <div className="p-2 border-t">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" className="w-full justify-start h-10">
              <div className="w-7 h-7 rounded-full bg-primary text-primary-foreground flex items-center justify-center mr-2 text-xs font-medium">
                {user?.name?.[0]?.toUpperCase() || "U"}
              </div>
              <span className="truncate flex-1 text-left text-sm">{user?.name || "Usuario"}</span>
              <MoreHorizontal className="w-4 h-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-56">
            <DropdownMenuLabel>Mi Cuenta</DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuItem>
              <User className="w-4 h-4 mr-2" />
              Perfil
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => window.location.href = "/settings"}>
              <Settings className="w-4 h-4 mr-2" />
              Configuración
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => window.location.href = "/api-keys"}>
              <Key className="w-4 h-4 mr-2" />
              API Keys
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => setShowImportDialog(true)}>
              <Download className="w-4 h-4 mr-2" />
              Importar Vault
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuLabel>Tema</DropdownMenuLabel>
            <DropdownMenuItem onClick={() => setNextTheme("light")}>
              <Sun className="w-4 h-4 mr-2" />
              Claro
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => setNextTheme("dark")}>
              <Moon className="w-4 h-4 mr-2" />
              Oscuro
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => setNextTheme("system")}>
              <Monitor className="w-4 h-4 mr-2" />
              Sistema
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            {user?.role === "admin" && (
              <>
                <DropdownMenuItem onClick={() => window.location.href = "/admin"}>
                  <Settings className="w-4 h-4 mr-2" />
                  Panel Admin
                </DropdownMenuItem>
                <DropdownMenuSeparator />
              </>
            )}
            <DropdownMenuItem onClick={handleLogout} className="text-destructive">
              <LogOut className="w-4 h-4 mr-2" />
              Cerrar Sesión
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      {/* Import Dialog */}
      <Dialog open={showImportDialog} onOpenChange={setShowImportDialog}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Importar Vault de Obsidian</DialogTitle>
            <DialogDescription>
              Importa todas tus notas, carpetas y adjuntos desde Obsidian
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <ObsidianImporterInline onClose={() => setShowImportDialog(false)} />
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit Workspace Dialog */}
      <Dialog open={!!editingWorkspace} onOpenChange={(open) => !open && setEditingWorkspace(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Renombrar Workspace</DialogTitle>
          </DialogHeader>
          <Input
            placeholder="Nombre del workspace"
            value={editWorkspaceName}
            onChange={(e) => setEditWorkspaceName(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && updateWorkspace()}
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditingWorkspace(null)}>
              Cancelar
            </Button>
            <Button onClick={updateWorkspace}>Guardar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Workspace Dialog */}
      <Dialog open={!!showDeleteConfirm} onOpenChange={(open) => !open && setShowDeleteConfirm(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Eliminar Workspace</DialogTitle>
            <DialogDescription>
              ¿Estás seguro de eliminar "{showDeleteConfirm?.name}"? Esta acción no se puede deshacer y se eliminarán todos los documentos.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDeleteConfirm(null)}>
              Cancelar
            </Button>
            <Button variant="destructive" onClick={deleteWorkspace}>
              Eliminar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </aside>
  );
}

// Inline importer component
function ObsidianImporterInline({ onClose }: { onClose: () => void }) {
  const [isImporting, setIsImporting] = useState(false);
  const [workspaceName, setWorkspaceName] = useState("Imported Vault");
  const { addWorkspace, setCurrentWorkspace } = useWorkspaceStore();

  const handleImport = async (e: React.FormEvent) => {
    e.preventDefault();
    const form = e.target as HTMLFormElement;
    const file = (form.elements.namedItem("file") as HTMLInputElement).files?.[0];

    if (!file) {
      toast.error("Selecciona un archivo .zip");
      return;
    }

    setIsImporting(true);

    try {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("workspaceName", workspaceName);

      const data = await api.post<{ success: boolean; imported: { notes: number }; workspace: any; error?: string }>('/import', formData);

      if (data.success) {
        toast.success(`¡Importado! ${data.imported.notes} notas`);
        addWorkspace(data.workspace);
        setCurrentWorkspace(data.workspace);
        onClose();
      } else {
        toast.error(data.error || "Error al importar");
      }
    } catch {
      toast.error("Error al importar");
    } finally {
      setIsImporting(false);
    }
  };

  return (
    <form onSubmit={handleImport} className="space-y-4">
      <div className="space-y-2">
        <label className="text-sm font-medium">Nombre del Workspace</label>
        <Input
          name="workspaceName"
          value={workspaceName}
          onChange={(e) => setWorkspaceName(e.target.value)}
          placeholder="Mi Vault Importado"
        />
      </div>

      <div className="space-y-2">
        <label className="text-sm font-medium">Archivo .zip</label>
        <Input
          name="file"
          type="file"
          accept=".zip"
          className="cursor-pointer"
        />
        <p className="text-xs text-muted-foreground">
          Exporta tu vault desde Obsidian: Configuración → Exportar → Exportar a ZIP
        </p>
      </div>

      <div className="flex justify-end gap-2 pt-4">
        <Button type="button" variant="outline" onClick={onClose}>
          Cancelar
        </Button>
        <Button type="submit" disabled={isImporting}>
          {isImporting ? "Importando..." : "Importar"}
        </Button>
      </div>
    </form>
  );
}
